import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, tweensAxis } from '../skillUsage';

export const italicSkewSwingSkill: CodeoSkill = {
    name: SKILL_NAMES.italicSkewSwing,
    kind: 'codeo',
    description:
        'Slide an italic word in from the side leaning back against its own slant, then let it ' +
        'swing forward into its italic as it stops, as if it had momentum — the closing beat ' +
        'that lands the answer ("Minutes").',
    checkLooksFor: 'a word sliding on x while its skewX is tweened back to 0',
    documentUsesSkill: check((script) => /skewX/.test(script) && tweensAxis(script, 'x')),
    instructions: `# Italic Skew Swing

The answer word arrives last and biggest, in the italic of the display face. It slides in from the left, fast, and **leans back** while it moves, as if its top were dragging behind. As it stops, it **swings forward into its italic**, and the slant is the word coming to rest. The slide and the swing end at slightly different times, so the stop has follow-through instead of a dead halt.

Filled with the brand gradient or the drifting mesh, it closes the video.

## Markup

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center"><div style="text-align: center; line-height: 1;">
  <span id="answer" style="display: inline-block; font-family: var(--l5-font-0), sans-serif; font-style: italic; font-weight: 400; font-size: 330px; color: var(--l5-color-2, #4b3ae6); transform-origin: 50% 100%;">Minutes</span>
</div></div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 1.3;
const CUE = 0;

const answer = document.querySelector('#answer');
const em = parseFloat(getComputedStyle(answer).fontSize);
// The slide: most of the distance in the first frames, settled in about ten frames at 24fps.
tl.fromTo(answer, { x: -0.8 * em }, { x: 0, duration: 0.42, ease: 'circ.out' }, CUE);
// The swing: leaning back 24° against the italic, easing forward to upright skew over half a second.
tl.fromTo(answer, { skewX: 24 }, { skewX: 0, duration: 0.5, ease: 'sine.out' }, CUE);

tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Gradient fill.** Fill the word with the key-word variant of \`${SKILL_NAMES.textureMaskedType}\`, or with \`${SKILL_NAMES.driftingMeshGround}\`'s blobs clipped to its letters, on a white ground.
- **Entering from the right.** Mirror \`x\` only and keep \`skewX\` from +24, so the word still leans back against its italic. It reads as less of a swing than the entrance from the left, so prefer the left.
- **The first frames.** If the incoming word would cross the outgoing phrase on the cut, hide its leading edge for the first two frames with \`clip-path: inset(0 0 0 0.45em)\` on its line, then clear it.
- **Lean.** 18–28°. Less reads as a wobble; more tears the letters.

## Pitfalls

- **Lean back, not forward.** Skewing with the italic doubles the slant and the word looks like it is falling over.
- **Skew from the baseline.** \`transform-origin: 50% 100%\` keeps the letters standing on their line while they swing; skewed from the centre the baseline slides.
- **Close on it.** It is the last beat: hold it to the end of the graphic.
- **Its first frame is its starting pose.** The tweens keep GSAP's default \`immediateRender\`, so the word is already left and leaning back on the frame the graphic opens.
- The word is transform-tweened, so it carries no CSS \`transform\`.`,
};
